import React, { useState, useCallback } from 'react'
import { Panel } from 'primereact/panel'
import { subDays } from 'date-fns'

import { enhancedApi, OcppLogsInfiniteApiArg } from '../../redux/api/enhancedWebApi'
import { OcppLogsTable } from '../ocpp-logs-table'
import useText from '../../hooks/useText'
import { LOG_TYPES_PARTNER, LOG_TYPES_SUPPORT, LogTypes, OCPP_LOGS_MAX_AGE_DAYS } from '../../constants/LogTypes'
import { isSupportDomain } from '../../../lib/currentDomain'

export type ChargePointLogsWidgetProps = {
    chargePointId: string
    showOcppRelayLogs?: boolean
}

export interface OcppLogFilters {
    actions?: string
    errorsOnly?: boolean
    from?: string
    to?: string
}

// Small first page for a fast initial load, large pages once the user interacts with the table
const INITIAL_PAGE_SIZE = 20
const PAGE_SIZE_AFTER_INTERACTION = 500

// Default filters to avoid double fetching
const DEFAULT_UNSELECTED_FILTERS: Array<LogTypes> = isSupportDomain
    ? [LogTypes.Heartbeat, LogTypes.MeterValues]
    : [LogTypes.StatusNotification]

const defaultActions = (isSupportDomain ? LOG_TYPES_SUPPORT : LOG_TYPES_PARTNER).map((type) => LogTypes[type])
const defaultActionFilter = defaultActions.filter(
    (type) => !DEFAULT_UNSELECTED_FILTERS.map((type) => LogTypes[type]).includes(type)
)

const getDefaultFilters = (): OcppLogFilters => ({
    actions: defaultActionFilter.join(','),
    errorsOnly: false,
})

const useOcppLogs = (source: OcppLogsInfiniteApiArg['source'], chargePointId: string, skip = false) => {
    const [filters, setFilters] = useState<OcppLogFilters>(getDefaultFilters())
    // Pins the open end of the requested time range, so pages stay stable while new logs arrive
    const [requestedAt, setRequestedAt] = useState(() => new Date())
    const [hasInteracted, setHasInteracted] = useState(false)

    const { data, isLoading, isFetching, hasNextPage, fetchNextPage, refetch } =
        enhancedApi.endpoints.ocppLogsInfinite.useInfiniteQuery(
            {
                source,
                chargePointId,
                from: filters.from ?? subDays(requestedAt, OCPP_LOGS_MAX_AGE_DAYS).toISOString(),
                to: filters.to ?? requestedAt.toISOString(),
                action: filters.actions?.split(','),
                errorsOnly: filters.errorsOnly,
                newestFirst: true,
                pageSize: hasInteracted ? PAGE_SIZE_AFTER_INTERACTION : INITIAL_PAGE_SIZE,
            },
            { skip }
        )

    const handleFiltersChange = useCallback((newFilters: OcppLogFilters) => {
        setFilters(newFilters)
        setRequestedAt(new Date())
        setHasInteracted(true)
    }, [])

    const handleLoadMore = useCallback(() => {
        if (!hasInteracted) {
            // Switching to large pages restarts from page 1, which also covers the rows already shown
            setHasInteracted(true)
            return
        }
        if (hasNextPage && !isFetching) {
            fetchNextPage()
        }
    }, [hasInteracted, hasNextPage, isFetching, fetchNextPage])

    const hasFixedTimeRange = Boolean(filters.from && filters.to)
    const handleRefresh = useCallback(() => {
        if (hasFixedTimeRange) {
            refetch()
        } else {
            setRequestedAt(new Date())
        }
    }, [hasFixedTimeRange, refetch])

    return {
        logs: data?.pages.flatMap((page) => page.data ?? []) ?? [],
        isLoading,
        isFetching,
        refetch: handleRefresh,
        onFiltersChange: handleFiltersChange,
        onLoadMore: hasNextPage ? handleLoadMore : undefined,
        hasMore: hasNextPage,
        filters,
    }
}

export function ChargePointOcppLogsWidget({
    chargePointId,
    showOcppRelayLogs,
}: ChargePointLogsWidgetProps): React.ReactElement {
    const text = useText()

    const gatewayLogs = useOcppLogs('gateway', chargePointId)
    const relayLogs = useOcppLogs('relay', chargePointId, !showOcppRelayLogs)

    if (showOcppRelayLogs) {
        return (
            <div className="grid">
                <div className="col-12">
                    <Panel
                        header={
                            <span className="mt-2 flex">
                                <i className="pi pi-receipt"></i>
                                <span className="ml-2">{text('lblOcppLogsWidget')}</span>
                            </span>
                        }
                        data-testid="ocpp-logs-widget"
                    >
                        <OcppLogsTable {...gatewayLogs} data-testid="ocpp-logs-table" height="calc(100vh - 475px)" />
                    </Panel>
                </div>
                <div className="col-12">
                    <Panel
                        header={
                            <span className="mt-2 flex">
                                <i className="pi pi-receipt"></i>
                                <span className="ml-2">{text('lblOcppRelayLogsWidget')}</span>
                            </span>
                        }
                        data-testid="relay-ocpp-logs-widget"
                    >
                        <OcppLogsTable
                            {...relayLogs}
                            height="calc(100vh - 475px)"
                            data-testid="relay-ocpp-logs-table"
                        />
                    </Panel>
                </div>
            </div>
        )
    }
    return <OcppLogsTable {...gatewayLogs} data-testid="ocpp-logs-table" />
}
